/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { downloadFile } from 'shared/utils/attachmentUtils';
import { callApi } from '../../api/helpers/api.helpers';
import draftApi from '../../api/draft.api';
import { RoutePath } from '../../shared/enums/RoutePath';
import useProcedure from '../../procedure/useProcedure';

export default function useRequestReceipt() {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();
  const [submission, setSubmission] = useState(null);
  const [dialogOpened, setDialogOpened] = useState(false);
  const [dialogError, setDialogError] = useState(false);
  const { control, handleSubmit, errors, formState, reset } = useForm({
    mode: 'onChange',
  });

  const currentProcedure = useProcedure();
  const match = useRouteMatch();
  const submissionId = match.params?.submissionId;

  useEffect(() => {
    (async () => {
      if (submissionId) {
        const draftTmp = await callApi(draftApi.getById)(submissionId);
        setSubmission(draftTmp);
      }
    })();
  }, [setSubmission, submissionId]);

  const emailRules = {
    validate: (value => value.length > 0 && isEmailListValid(value)) || t('user-management.user-email.required'),
    required: t('user-management.user-email.required'),
  };

  const confirmRequest = async (confirmData) => {
    await callApi(draftApi.requestReceipt, {
      onError: () => {
        setDialogError(true);
      },
      onSuccess: (response) => {
        setDialogError(!response);
      },
    })(submissionId, confirmData.email);

    setDialogOpened(true);
  };

  const handleSendAnother = () => {
    reset();
    setDialogOpened(false);
  };

  const handleGoToOverview = () => {
    const params = {
      procedure: submission?.procedure,
      ...(submission?.initialSubmissionProcedure && {
        initialSubmissionProcedure: submission?.initialSubmissionProcedure,
      }),
    };
    history.push(RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, params));
  };

  const downloadReceiptFile = async () => {
    if (submission && submission.receipt) {
      const fileName = submission.receipt?.receiptFile.id;
      const [filePath] = await callApi(draftApi.openFile)(
        submission.id,
        submission.receipt.receiptFile.id,
        'Receipt',
        'application/pdf',
        '/receipt',
      );

      downloadFile(filePath, fileName);
    }
  };

  const isEmailListValid = (emailList) => {
    const validations = emailList?.map(email => validateEmail(email));
    return validations?.every(item => item === true);
  };

  const validateEmail = (value) => {
    if (!value) return true;

    return typeof value === 'object' || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value);
  };

  return {
    confirmRequest,
    control,
    dialogError,
    dialogOpened,
    emailRules,
    errors,
    formState,
    downloadReceiptFile,
    handleGoToOverview,
    handleSendAnother,
    handleSubmit,
    validateEmail,
    location,
    submission,
    currentProcedure,
  };
}
